import { useEffect, useState } from "react";
import { CalendarCheck, Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { Term } from "@/components/Term";

const list = (obj) => Object.entries(obj || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "none";
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) : "—");

// 12-week growth of the evidence base: sealed (new records) vs graded (outcomes issued) per ISO week.
const WeeklyChart = ({ history }) => {
  const total = history.reduce((a, h) => a + h.sealed + h.graded, 0);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="weekly-evidence-chart" data-weeks={history.length} data-total={total}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
        <span>Last {history.length} weeks</span>
        <span className="flex items-center gap-3 normal-case tracking-normal"><span className="flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-sm bg-sky-400" /> sealed</span><span className="flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-sm bg-emerald-400" /> graded</span></span>
      </div>
      {total === 0 ? <div className="h-[72px] flex items-center justify-center text-xs text-slate-500">No sealed or graded records in this window yet.</div> : (
        <div className="h-[72px] mt-1">
          <ResponsiveContainer>
            <BarChart data={history} barGap={1} barCategoryGap="25%" margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="week" tick={{ fontSize: 9, fill: "#64748b" }} tickFormatter={(w) => w.slice(-3)} axisLine={false} tickLine={false} interval={1} />
              <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 11 }} labelFormatter={(w, p) => `${w} · ${p?.[0]?.payload?.from} → ${p?.[0]?.payload?.to}`} />
              <Bar dataKey="sealed" fill="#38bdf8" isAnimationActive={false} radius={[2, 2, 0, 0]} />
              <Bar dataKey="graded" fill="#34d399" isAnimationActive={false} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, sub, tid, k }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid={tid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500"><Term k={k} testid={`${tid}-term`} iconClass="normal-case">{label}</Term></div>
    <div className="font-mono-ticker text-xl text-slate-100 mt-0.5">{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{sub}</div>}
  </div>
);

// The same figures the owner receives in the Monday evidence digest, read live from the sealed ledger (last 7 days).
export const WeeklyEvidenceSummary = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/predictions/weekly-summary").then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load weekly summary")); }, []);
  const p = d?.progress;
  return (
    <div className="apx-card p-5 md:p-6 space-y-3" data-testid="weekly-evidence-summary">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display font-semibold text-base text-slate-100"><CalendarCheck className="w-5 h-5 text-emerald-300" /> Weekly evidence summary</div>
        {d && <div className="text-[11px] text-slate-500 font-mono-ticker" data-testid="weekly-summary-window">{fmtDay(d.since)} → {fmtDay(d.until)}{d.last_digest ? ` · last digest e-mailed ${d.last_digest.week}` : " · no digest e-mailed yet"}</div>}
      </div>
      <p className="text-xs text-slate-400 max-w-3xl">Identical to the Monday e-mail digest: counts are read from the sealed ledger for the last 7 days — nothing is estimated or back-filled. The Prediction Accuracy Score is published only once both thresholds below are met.</p>
      {err && <div className="text-sm text-rose-200" data-testid="weekly-summary-error">{err}</div>}
      {!d && !err && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
      {d && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat tid="weekly-sealed" k="weekly_sealed" label="New sealed records" value={d.sealed.total} sub={`origin: ${list(d.sealed.by_origin)} · source: ${list(d.sealed.by_source)}`} />
          <Stat tid="weekly-graded" k="weekly_graded" label="Graded this week" value={d.graded.total} sub={`${list(d.graded.by_outcome)} · directional ${d.graded.directional} · invalidated ${d.graded.invalidated}`} />
          <Stat tid="weekly-progress-graded" k="ledger_thresholds" label="Independent graded" value={`${p.independent_graded} / ${p.need_graded}`} sub={p.remaining_graded ? `${p.remaining_graded} more needed` : "threshold met"} />
          <Stat tid="weekly-progress-directional" k="ledger_directional" label="Independent directional" value={`${p.independent_directional} / ${p.need_directional}`} sub={p.remaining_directional ? `${p.remaining_directional} more needed` : "threshold met"} />
          <Stat tid="weekly-pending" k="weekly_pending" label="Still pending" value={p.pending} sub="graded automatically at their horizon (05:00 UTC)" />
          </div>
          {d.history && <WeeklyChart history={d.history} />}
        </>
      )}
    </div>
  );
};
